/**
 * App menu items driven by the route registry.
 */

import type { RouteId, RouteRole } from './types'
import { getRouteById } from './route-registry'
import { canAccessRoute } from './can-access-route'
import type { ContextScope } from '@/types/active-context'

export interface AppNavItem {
  id: RouteId
  labelKey?: string
  label?: string
  path: string
  /** Show even when effectiveRole differs (actual admins always see admin link). */
  actualAdminOnly?: boolean
}

export const APP_NAV_ITEMS: AppNavItem[] = [
  { id: 'analyzer', labelKey: 'appMenu.askLina', path: '/analyze' },
  { id: 'lens', label: 'LINA Lens', path: '/lens' },
  { id: 'org', label: 'Organization', path: '/org/dashboard' },
  { id: 'admin', labelKey: 'appMenu.adminDashboard', path: '/admin', actualAdminOnly: true },
]

export interface NavVisibilityInput {
  actualRole: RouteRole
  effectiveRole: RouteRole
  activeContextScope?: ContextScope | null
  hasOrganizationContext: boolean
  isAnonymous?: boolean
  isActualAdmin: boolean
}

export function canShowNavItem(item: AppNavItem, input: NavVisibilityInput): boolean {
  const route = getRouteById(item.id)
  if (!route) return false

  if (item.actualAdminOnly) {
    return input.isActualAdmin
  }

  return canAccessRoute({
    route,
    actualRole: input.actualRole,
    effectiveRole: input.effectiveRole,
    activeContextScope: input.activeContextScope,
    isAnonymous: input.isAnonymous,
  })
}

export function listVisibleNavItems(input: NavVisibilityInput): AppNavItem[] {
  return APP_NAV_ITEMS.filter((item) => canShowNavItem(item, input))
}

/** Hide nav link when user is already in that section. */
export function isNavItemActiveSection(item: AppNavItem, pathname: string): boolean {
  if (item.id === 'analyzer') {
    return pathname === '/analyze' || pathname.startsWith('/analyze/')
  }
  if (item.id === 'org') {
    return pathname.startsWith('/org')
  }
  if (item.id === 'admin') {
    return pathname.startsWith('/admin')
  }
  return pathname === item.path || pathname.startsWith(`${item.path}/`)
}
